import {
    cleanupVideoInput,
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
    prepareVideoInput,
} from '../compress/compressVideoEngine';
import { formatVideoTime } from '../trim/trimVideoEngine';

export {
    formatFileSize,
    formatVideoTime,
    isSupportedVideoFile,
};

export const MAX_TO_GIF_VIDEO_BYTES = 200 * 1024 * 1024;
export const MIN_TRIM_GAP_SECONDS = 0.2;
export const DEFAULT_FPS = 12;
export const DEFAULT_SIZE = '480';
export const DEFAULT_DITHERING = 'floyd_steinberg';

export const GIF_SIZE_PRESETS = [
    { id: '0', maxWidth: 0 },
    { id: '640', maxWidth: 640 },
    { id: '480', maxWidth: 480 },
    { id: '320', maxWidth: 320 },
    { id: '240', maxWidth: 240 },
    { id: '160', maxWidth: 160 },
];

export const DITHERING_OPTIONS = [
    'floyd_steinberg',
    'sierra2_4a',
    'bayer',
    'none',
];

export function toGifVideoName(fileName) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    return `${base}.gif`;
}

/**
 * @param {File} file
 * @param {{
 *   maxWidth?: number,
 *   fps?: number,
 *   dithering?: string,
 *   startTime?: number,
 *   duration?: number,
 * }} options
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Blob>}
 */
export async function videoToGif(file, options = {}, onProgress = () => {}) {
    const {
        maxWidth = 480,
        fps = DEFAULT_FPS,
        dithering = DEFAULT_DITHERING,
        startTime = 0,
        duration = 0,
    } = options;

    let ffmpeg;
    let videoInput = null;
    const paletteName = 'palette.png';
    const outputName = 'output.gif';

    const safeDelete = async (name) => {
        try { await ffmpeg.deleteFile(name); } catch (_) { /* ignore */ }
    };

    try {
        ffmpeg = await loadFFmpeg();
        videoInput = await prepareVideoInput(ffmpeg, file);

        const scaleExpr = maxWidth > 0
            ? `scale='min(${maxWidth}\\,iw)':-1:flags=lanczos`
            : 'scale=iw:-1:flags=lanczos';

        const paletteProgressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 50, 50)));
        };
        ffmpeg.on('progress', paletteProgressHandler);

        const paletteArgs = [];
        if (startTime > 0) {
            paletteArgs.push('-ss', String(startTime));
        }
        paletteArgs.push('-i', videoInput.inputPath);
        if (duration > 0) {
            paletteArgs.push('-t', String(duration));
        }
        paletteArgs.push('-vf', `fps=${fps},${scaleExpr},palettegen`, paletteName);

        const paletteExit = await ffmpeg.exec(paletteArgs);
        ffmpeg.off('progress', paletteProgressHandler);

        if (paletteExit !== 0) {
            throw new Error('ENCODE_FAILED');
        }

        const gifProgressHandler = ({ progress }) => {
            onProgress(Math.round(50 + Math.min(progress * 50, 50)));
        };
        ffmpeg.on('progress', gifProgressHandler);

        const gifArgs = [];
        if (startTime > 0) {
            gifArgs.push('-ss', String(startTime));
        }
        gifArgs.push('-i', videoInput.inputPath, '-i', paletteName);
        if (duration > 0) {
            gifArgs.push('-t', String(duration));
        }
        gifArgs.push(
            '-lavfi',
            `fps=${fps},${scaleExpr}[x];[x][1:v]paletteuse=dither=${dithering}`,
            '-loop', '0',
            outputName,
        );

        const gifExit = await ffmpeg.exec(gifArgs);
        ffmpeg.off('progress', gifProgressHandler);

        if (gifExit !== 0) {
            throw new Error('ENCODE_FAILED');
        }

        const data = await ffmpeg.readFile(outputName);
        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'image/gif' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (error?.message === 'ENCODE_FAILED' || error?.message === 'EMPTY_OUTPUT') {
            throw error;
        }
        throw new Error('GIF_FAILED');
    } finally {
        if (ffmpeg) {
            if (videoInput) {
                await cleanupVideoInput(ffmpeg, videoInput);
            }
            await safeDelete(paletteName);
            await safeDelete(outputName);
        }
    }
}
